import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Transition } from '@headlessui/react'
import { logout } from '../../store/authSlice'
import { getAvatarText } from '../../utils/user'

const MenuItem = ({ icon, label, path, current }) => (
  <Link
    to={path}
    className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg ${
      current
        ? 'bg-blue-100 text-blue-700'
        : 'text-gray-700 hover:bg-gray-100'
    }`}
  >
    <span className="mr-3">{icon}</span>
    {label}
  </Link>
)

const MainLayout = ({ children }) => {
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showLogoutMenu, setShowLogoutMenu] = useState(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector(state => state.auth.user)
  
  const menuItems = [
    {
      icon: '📊',
      label: 'Dashboard',
      path: '/'
    },
    {
      icon: '🌐',
      label: 'Website Config',
      path: '/website-config'
    },
    // {
    //   icon: '🎟️',
    //   label: 'Coupon Trigger',
    //   path: '/coupon-trigger'
    // },
    {
      icon: '📱',
      label: 'Phone Groups',
      path: '/phone-groups'
    },
    {
      icon: '🧪',
      label: 'Algorithm Test',
      path: '/algorithm-test'
    }
  ]

  const handleLogout = async () => {
    await dispatch(logout())
    navigate('/login')
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* 桌面端左侧菜单 */}
      <div className="hidden md:block w-64 bg-white shadow-sm">
        {/* Logo */}
        <div className="px-6 py-4 border-b">
          <h1 className="text-xl font-bold text-gray-800">Webhook Admin</h1>
        </div>

        {/* 菜单项 */}
        <nav className="p-4 space-y-1">
          {menuItems.map((item) => (
            <MenuItem
              key={item.path}
              {...item}
              current={location.pathname === item.path}
            />
          ))}
        </nav>
      </div>

      {/* 右侧内容区域 */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 顶部导航栏 */}
        <header className="bg-white shadow-sm">
          <div className="px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              {/* 移动端汉堡菜单按钮 */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-md text-gray-500 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {isMobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>

              <h2 className="text-lg font-medium text-gray-900">
                {menuItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
              </h2>

              {/* 用户头像和退出菜单 */}
              <div className="relative">
                <button
                  onClick={() => setShowLogoutMenu(!showLogoutMenu)}
                  className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100 focus:outline-none"
                >
                  <div 
                    className="h-8 w-8 rounded-full bg-brand-primary flex items-center justify-center" 
                    style={user?.avatar ? {
                      backgroundImage: `url(${user.avatar})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    } : undefined}
                  >
                    {!user?.avatar && (
                      <span className="text-white text-sm font-medium">
                        {getAvatarText(user?.name)}
                      </span>
                    )}
                  </div>
                </button>

                {/* 退出菜单 */}
                {showLogoutMenu && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                    <div className="py-1">
                      {user && (
                        <div className="px-4 py-2 text-sm text-gray-700 border-b">
                          <div className="font-medium">{user.name}</div>
                          <div className="text-gray-500 text-xs mt-0.5">{user.email}</div>
                        </div>
                      )}
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* 移动端菜单 */}
        <Transition
          show={isMobileMenuOpen}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 -translate-x-full"
          enterTo="opacity-100 translate-x-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-x-0"
          leaveTo="opacity-0 -translate-x-full"
          className="md:hidden fixed inset-0 z-40"
        >
          {/* 背景遮罩 */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-25"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* 菜单面板 */}
          <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-lg">
            <div className="px-6 py-4 border-b">
              <h1 className="text-xl font-bold text-gray-800">Webhook Admin</h1>
            </div>
            
            <nav className="p-4 space-y-1">
              {menuItems.map((item) => (
                <div key={item.path} onClick={() => setIsMobileMenuOpen(false)}>
                  <MenuItem
                    {...item}
                    current={location.pathname === item.path}
                  />
                </div>
              ))}
            </nav>
          </div>
        </Transition>

        {/* 主要内容区域 */}
        <main className="flex-1 overflow-auto">
          <div className="p-4 sm:p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default MainLayout 